/* eslint-disable import/no-named-as-default */
import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Toolbar from "@mui/material/Toolbar";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";

import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";

import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import utils from "./../../universal/utils";
import { toProfilePath } from "../../universal/v2Routes";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PersonIcon from "@mui/icons-material/Person";
import BarChartIcon from "@mui/icons-material/BarChart";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SettingsIcon from "@mui/icons-material/Settings";
import HomeIcon from "@mui/icons-material/Home";
import FlagIcon from "@mui/icons-material/Flag";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HistoryIcon from "@mui/icons-material/History";
import Chip from "@mui/material/Chip";
import NotificationBadge from "../notifications/NotificationBadge";
import NotificationPanel from "../notifications/NotificationPanel.jsx";
import FriendsModal from "../friends/FriendsModal";
import PeopleIcon from "@mui/icons-material/People";

// import { getCartItemsCount } from "./../../redux/reducers/utils/cart";
import Badge from "@mui/material/Badge";

import { GoogleLogin } from "@react-oauth/google";
import { hasExplicitLogout } from "../../universal/explicitLogoutGuard";

const basePath = utils.getEdgeBasePath();
const { fetchJsonWithDiagnostics } = utils;

export default function AppMenuBar({
  isAuthenticated,
  autoLoginInProgress = false,
  googleResponse,
  actions,
  onFailure,
  user = {},
  catalog,
  logout,
}) {
  const location = useLocation();
  const logoutBlocked = (() => {
    if (catalog?.lobbyState === "searching") return true;
    const isLeader = user && catalog?.currentLobby ? catalog.currentLobby.hostId === user.userId : false;
    if (catalog?.matchmakingStarted && !isLeader && catalog?.currentLobbyCode) return true;
    return false;
  })();

  const sx = {
    root: { display: "flex", justifyContent: "center" },
    appBar: (theme) => ({
      width: "100%",
      background: "#0b0b0b",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: theme.zIndex?.appBar || 1100,
    }),
    appBarInner: {
      width: "104%",
      boxSizing: "border-box",
      px: { xs: 2, sm: 3 },
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      transition: "all 0.3s ease",
      minHeight: 64,
      width: "100%",
      height: { xl: "5rem" },
    },
    logoOuroContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
    logo: {
      display: "flex",
      alignItems: "center",
      mr: "0.5rem",
    },
    logoImg: {
      height: "2.2rem",
      display: "block",
      backgroundColor: "transparent",
      transition: "all 0.3s ease",
    },
    betaChip: {
      height: "auto",
      ml: "0.4rem",
      display: "flex",
      alignItems: "center",
      "& .MuiChip-label": {
        px: "0.4rem",
        py: "0.15rem",
        fontSize: { xs: "0.6rem", xl: "0.7rem" },
        fontWeight: 600,
        letterSpacing: "0.08em",
        lineHeight: 1,
      },
      borderColor: "#444444",
      color: "#E8A33D",
    },
    contentBarContainer: { display: "flex", alignItems: "center" },
    contentBarLayout: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      gap: "1.5rem",
    },
    googleLoginContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    notificationContainer: { ml: 1, display: "flex", alignItems: "center" },
    notificationButton: {
      width: { xs: "2.25rem", xl: "2.7rem" },
      height: { xs: "2.25rem", xl: "2.7rem" },
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#1a1a1a",
      color: "#f0f0f0",
      transition: "background-color 150ms ease-in-out, box-shadow 150ms ease-in-out",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#222222",
        boxShadow: "0 0 0 2px rgba(255,255,255,0.1)",
      },
    },
    notificationIcon: { fontSize: { xs: "1.25rem", xl: "1.4rem" } },
    badgeSx: {
      "& .MuiBadge-badge": {
        backgroundColor: "#E8A33D",
        color: "#ffffff",
        minWidth: { xs: "1.125rem", xl: "1.4rem" },
        height: { xs: "1.125rem", xl: "1.4rem" },
        px: { xs: "0.3rem", xl: "0.4rem" },
        borderRadius: "50%",
        fontSize: { xs: "0.625rem", xl: "0.75rem" },
        fontWeight: 700,
        boxShadow: "0 0 0 2px #0b0b0b",
      },
    },
    containerAvatarElo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.5rem",
    },
    eloMenuCont: { display: "flex", alignItems: "center" },
    eloPill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      px: "0.52rem",
      py: "0.3rem",
      borderRadius: "5px",
      backgroundColor: "rgba(28, 30, 32, 0.9)",
      border: "1px solid rgba(232, 163, 61, 0.4)",
      boxSizing: "border-box",
      flexShrink: 0,
      userSelect: "none",
    },
    eloText: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "0.82rem",
      fontWeight: 700,
      color: "#E8A33D",
      lineHeight: 1,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap",
    },
    avatar: {
      width: { xs: "2.5rem", xl: "3.2rem" },
      height: { xs: "2.5rem", xl: "3.2rem" },
      fontWeight: 600,
      fontSize: "1rem",
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      borderRadius: "4px",
      border: "1px solid rgba(232, 163, 61, 0.4)",
      "&:hover": { boxShadow: "0 0 0 2px rgba(232, 163, 61, 0.4)" },
    },
    menuPaper: {
      backgroundColor: "#0b0b0b",
      border: "1px solid #666666",
      borderRadius: "0.5rem",
      p: "0.5rem",
      minWidth: "14rem",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
      transition: "all 0.3s ease",
    },
    menuItem: {
      px: "0.75rem",
      py: "0.35rem",
      color: "#ffffff",
      transition: "all 200ms ease-in-out",
      "&:hover": { backgroundColor: "#1a1a1a" },
      "& .MuiListItemIcon-root": {
        color: "#ffffff",
        minWidth: "2rem",
        "& .MuiSvgIcon-root": { fontSize: "1.15rem" },
      },
      "& .MuiListItemText-primary": {
        color: "#ffffff",
        fontSize: "0.84rem",
        fontWeight: 500,
      },
    },
    menuItemDisabled: { opacity: 0.5, color: "#999999" },
    userInfoSection: {
      p: "0.65rem 0.75rem",
      backgroundColor: "#111111",
      borderBottom: "1px solid #333333",
      mb: "0.25rem",
    },
    userInfoContainer: {
      display: "flex",
      alignItems: "center",
      gap: "0.6rem",
    },
    avatarMenu: {
      width: "2.2rem",
      height: "2.2rem",
      fontWeight: 600,
      fontSize: "0.85rem",
      transition: "all 0.2s ease-in-out",
      borderRadius: "4px",
      border: "1px solid rgba(232, 163, 61, 0.4)",
    },
    userInfoText: { flex: 1 },
    userNickname: {
      color: "#ffffff",
      fontSize: "0.9rem",
      fontWeight: 700,
      textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
      letterSpacing: "0.5px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "7rem",
    },
    userFullName: {
      textTransform: "capitalize",
      color: "#999999",
      fontSize: "0.78rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "7rem",
    },
    countryFlag: {
      width: "1.4rem",
      height: "1.05rem",
      borderRadius: "2px",
      objectFit: "cover",
    },
    menuDivider: { backgroundColor: "#333333", my: "0.3rem" },
    menuSectionLabel: {
      fontSize: "0.65rem",
      color: "#999999",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      padding: "0.25rem 0.75rem",
      fontWeight: 600,
    },
    menuItemLogout: {
      px: "0.75rem",
      py: "0.35rem",
      color: "#E8A33D",
      transition: "all 200ms ease-in-out",
      "&:hover": { backgroundColor: "#1a1a1a" },
      "& .MuiListItemIcon-root": {
        color: "#E8A33D",
        minWidth: "2rem",
        "& .MuiSvgIcon-root": { fontSize: "1.15rem" },
      },
      "& .MuiListItemText-primary": {
        color: "#E8A33D",
        fontSize: "0.84rem",
        fontWeight: 500,
      },
    },
  };

  const { friendRequests, lobbyState, matchmakingStarted, currentLobby, currentLobbyCode, friends } = catalog;
  const [countryFlag, setCountryFlag] = React.useState(null);
  
  const isLobbyPage = location.pathname === '/lobby';
  const isLeader = user && currentLobby ? currentLobby.hostId === user.userId : false;
  const avatarVersion = pickAvatarUpdatedAtMs(user, catalog?.avatarCacheBust);
  
  // Debug logging for user object
  React.useEffect(() => {
    if (user) {
      console.log('🔝 [Menu] User data received:', {
        userId: user.userId,
        email: user.email,
        nickname: user.nickname,
        hasAvatar: user.hasOwnProperty('avatar'),
        avatarValue: user.avatar,
        picture: user.picture,
        avatarUpdatedAtMs: avatarVersion,
      });
    }
  }, [user, avatarVersion]);

  const avatarSrc = resolveAvatarSrc({
    basePath,
    picture: user?.picture,
    userId: user?.userId || user?._id,
    avatarUpdatedAtMs: avatarVersion,
    hasCustomAvatar: !!user?.avatar,
  });
  const ownProfilePath = toProfilePath(user?._id, location.pathname);
  const myReputationPath = ownProfilePath
    ? `${ownProfilePath}?tab=reputation`
    : "/account/reputation";
  const unreadCountDebugContext = {
    route: location.pathname,
    userId: user?.userId || null,
    mongoUserId: user?._id || null,
  };
    
  if (user) {
    console.log('🔝 [Menu] Avatar URL decision:', {
      hasCustomAvatarPicture:
        user?.picture && user.picture.includes('/api/v1/media/avatar/'),
      avatarUpdatedAtMs: avatarVersion,
      finalAvatarSrc: avatarSrc,
    });
  }

  React.useEffect(() => {
    actions.getTrackingVisitorId();
  }, [user]);

  // Fetch unread notification count
  React.useEffect(() => {
    if (user && user._id) {
      const fetchUnreadCount = async () => {
        const result = await fetchJsonWithDiagnostics(
          `${basePath}/api/v1/notifications/unread-count`,
          {
            credentials: 'include',
          },
          {
            context: 'Menu unread notification count',
            extra: unreadCountDebugContext,
          }
        );

        if (result.ok && typeof result.data?.count === 'number') {
          setUnreadNotificationCount(result.data.count);
        }
      };

      fetchUnreadCount();
      // Poll every 30 seconds for new notifications
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  React.useEffect(() => {
    const fetchCountryFlag = async () => {
      if (user && user.country && !user.flagImageUrl) {
        try {
          const response = await fetch(`${basePath}/api/v1/lobby/country-flag/${user.country}`);
          const data = await response.json();
          if (data.ok) {
            setCountryFlag(data.country.flagImageUrl);
          }
        } catch (error) {
          console.error('Failed to fetch country flag:', error);
        }
      } else if (user && user.flagImageUrl) {
        setCountryFlag(user.flagImageUrl);
      }
    };

    fetchCountryFlag();
  }, [user?.country, user?.flagImageUrl]);

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [notificationPanelOpen, setNotificationPanelOpen] = React.useState(false);
  const notificationAnchorRef = React.useRef(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = React.useState(0);
  const [friendsModalOpen, setFriendsModalOpen] = React.useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleLogout = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    
    // Use setTimeout with delay to ensure menu is completely closed before logout
    setTimeout(() => {
      logout();
    }, 300); 
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      if (anchorRef.current) {
        anchorRef.current.focus();
      }
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <Box sx={sx.root}>
      <AppBar position="fixed" sx={sx.appBar} elevation={0}>
        <Container maxWidth="lg" disableGutters={false}>
          <Box sx={sx.appBarInner}>
            <Toolbar sx={sx.toolbar} disableGutters>
              <Box sx={sx.logoOuroContainer}>
                <Box component="a" href="/" sx={sx.logo}>
                  <Box
                    component="img"
                    alt="Sportyshop"
                    src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png"
                    sx={sx.logoImg}
                  />
                </Box>
                <Chip label="BETA" variant="outlined" size="small" sx={sx.betaChip} />
              </Box>
              <Box sx={sx.contentBarContainer}>
                {isAuthenticated && user ? (
                  <React.Fragment>
                    <Box sx={sx.contentBarLayout}>
                      {/* Friend Requests Badge (only on lobby page) */}
                      {isLobbyPage && friendRequests?.length > 0 && (
                        <Box
                          role="button"
                          sx={sx.notificationContainer}
                          onClick={() => {
                            actions.setFriendSidebarTab('request');
                            actions.setFriendSidebarOpen(true);
                          }}
                        >
                          <Badge 
                            badgeContent={friendRequests.length} 
                            color="secondary"
                            sx={sx.badgeSx}
                            overlap="circular"
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <Box sx={sx.notificationButton}>
                              <PersonAddIcon sx={sx.notificationIcon} />
                            </Box>
                          </Badge>
                        </Box>
                      )}

                      {/* Friends Icon */}
                      <Box
                        role="button"
                        onClick={() => setFriendsModalOpen(true)}
                        sx={{
                          ml: 1,
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <Badge
                          badgeContent={friends?.filter(f => f.friendData?.isOnline).length || 0}
                          color="secondary"
                          overlap="circular"
                          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                          sx={sx.badgeSx}
                        >
                          <Box sx={sx.notificationButton}>
                            <PeopleIcon sx={sx.notificationIcon} />
                          </Box>
                        </Badge>
                      </Box>

                      {/* Notifications Badge (always visible) */}
                      <Box ref={notificationAnchorRef}>
                        <NotificationBadge
                          count={unreadNotificationCount}
                          onClick={() => setNotificationPanelOpen(!notificationPanelOpen)}
                        />
                      </Box>

                      <Box sx={sx.containerAvatarElo}>
                        <Box component="span" sx={sx.eloMenuCont}>
                          <Box sx={sx.eloPill}>
                            <Typography component="span" sx={sx.eloText}>
                              ELO {user?.elo ?? "—"}
                            </Typography>
                          </Box>
                        </Box>

                        {user && user._id && (
                          <Avatar
                            sx={sx.avatar}
                            alt={user.name}
                            src={avatarSrc}
                            ref={anchorRef}
                            aria-controls={open ? "menu-list-grow" : undefined}
                            aria-haspopup="true"
                            onClick={handleToggle}
                          />
                        )}
                      </Box>                  
                    </Box>

                  </React.Fragment>
                ) : autoLoginInProgress ? null : (
                  <Box sx={sx.googleLoginContainer}>
                    <GoogleLogin
                      width={188}
                      onSuccess={googleResponse}
                      onError={onFailure}
                      auto_select={!hasExplicitLogout()}
                      useOneTap={!hasExplicitLogout()}
                    />
                  </Box>
                )}
              </Box>      
            </Toolbar>
          </Box>
        </Container>
      </AppBar>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        style={{ zIndex: 10000, marginTop: '0.5rem' }}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => {
          return (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom-end" ? "top right" : "bottom right",
              }}
            >
              <Paper sx={sx.menuPaper}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="menu-list-grow"
                    onKeyDown={handleListKeyDown}
                    sx={{ p: 0 }}
                  >
                    {/* Section 1: User Info */}
                    <Box sx={sx.userInfoSection}>
                      <Box sx={sx.userInfoContainer}>
                        <Avatar
                          src={avatarSrc}
                          alt={user?.name || 'User'}
                          sx={sx.avatarMenu}
                        />
                        <Box sx={sx.userInfoText}>
                          <Box>
                            <Typography sx={sx.userNickname}>
                              {user?.nickname || user?.name || 'User'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={sx.userFullName}>
                              {user?.given_name && user?.family_name 
                                ? `${user.given_name} ${user.family_name}`
                                : user?.nickname || user?.name || 'User'
                              }
                            </Typography>
                          </Box>
                        </Box>
                        {countryFlag && (
                          <Box
                            component="img"
                            src={countryFlag}
                            alt={user?.country || 'Country'}
                            sx={sx.countryFlag}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Section 2: Quick Profile */}
                    <MenuItem 
                      component={Link} 
                      to={`/profile/${user?._id}`} 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText>
                        View Profile
                      </ListItemText>
                    </MenuItem>

                    <Divider sx={sx.menuDivider} />

                    {/* Section 3: Navigation */}
                    <MenuItem 
                      component={Link} 
                      to="/lobby" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <HomeIcon />
                      </ListItemIcon>
                      <ListItemText>
                        Lobby
                      </ListItemText>
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/matches"
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <HistoryIcon />
                      </ListItemIcon>
                      <ListItemText>
                        Latest Matches
                      </ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={Link} 
                      to="/leaderboard" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <EmojiEventsIcon />
                      </ListItemIcon>
                      <ListItemText>
                        Rankings
                      </ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={Link} 
                      to="/players" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <PersonSearchIcon />
                      </ListItemIcon>
                      <ListItemText>
                        Find Player
                      </ListItemText>
                    </MenuItem>

                    <Divider sx={sx.menuDivider} />

                    {/* Section 4: Account & Settings */}
                    <Typography sx={sx.menuSectionLabel}>ACCOUNT</Typography>
                    <MenuItem 
                      component={Link} 
                      to="/account" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText>
                        Account Settings
                      </ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={Link} 
                      to="/tactical-profile" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <SportsEsportsIcon />
                      </ListItemIcon>
                      <ListItemText>
                        CS Skills
                      </ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={Link} 
                      to={myReputationPath}
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <VerifiedUserIcon />
                      </ListItemIcon>
                      <ListItemText>
                        My Reputation
                      </ListItemText>
                    </MenuItem>

                    <Divider sx={sx.menuDivider} />

                    {/* Section 5: Support */}
                    <Typography sx={sx.menuSectionLabel}>SUPPORT</Typography>
                    <MenuItem 
                      component={Link} 
                      to="/account/tickets" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <SupportAgentIcon />
                      </ListItemIcon>
                      <ListItemText>
                        Support Tickets
                      </ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={Link} 
                      to="/reports" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <FlagIcon />
                      </ListItemIcon>
                      <ListItemText>
                        My Reports
                      </ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={Link} 
                      to="/reputation" 
                      sx={sx.menuItem}
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <VerifiedUserIcon />
                      </ListItemIcon>
                      <ListItemText>
                        Reputation System
                      </ListItemText>
                    </MenuItem>

                    <Divider sx={sx.menuDivider} />

                    {/* Section 6: Logout */}
                    <MenuItem
                      sx={[sx.menuItemLogout, logoutBlocked && sx.menuItemDisabled]}
                      onClick={(event) => {
                      if (logoutBlocked) {
                        actions.showUnavailableActionMessage();
                      } else {
                        handleLogout(event);
                      }
                    }}>
                      <ListItemIcon>
                        <ExitToAppIcon />
                      </ListItemIcon>
                      <ListItemText>Logout</ListItemText>
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          );
        }}
      </Popper>

      {/* Notification Panel */}
      <NotificationPanel
        open={notificationPanelOpen}
        anchorEl={notificationAnchorRef.current}
        onClose={() => {
          setNotificationPanelOpen(false);
          // Refetch unread count after closing
          if (user && user._id) {
            fetchJsonWithDiagnostics(
              `${basePath}/api/v1/notifications/unread-count`,
              {
                credentials: 'include',
              },
              {
                context: 'Menu unread notification count after panel close',
                extra: unreadCountDebugContext,
              }
            ).then((result) => {
              if (result.ok && typeof result.data?.count === 'number') {
                setUnreadNotificationCount(result.data.count);
              }
            });
          }
        }}
      />

      {/* Friends Modal */}
      <FriendsModal
        open={friendsModalOpen}
        onClose={() => setFriendsModalOpen(false)}
        friends={catalog?.friends || []}
        user={user}
      />
    </Box>
  );
}

AppMenuBar.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  autoLoginInProgress: PropTypes.bool,
  googleResponse: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object,
  user: PropTypes.object,
  logout: PropTypes.func,
};
